import styled, { keyframes } from 'styled-components';

export const color = {
  primary: '#00808A',
  primaryHover: '#00656E',
  primaryLight: '#E5F3F4',
  text: '#1B2630',
  textSub: '#44505A',
  textMuted: '#5B6770',
  border: '#DDE4E8',
  borderLight: '#E6EBED',
  bg: '#F1F5F7',
  bgSoft: '#F7F9FA',
  warnBg: '#FFF1D6',
  warnText: '#7A4E00',
};

// Layout
export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

export const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 32px;
  background-color: #fff;
  border-bottom: 1px solid ${color.borderLight};
`;

export const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${color.textMuted};

  strong {
    font-weight: 700;
    color: ${color.primary};
    letter-spacing: 0.08em;
  }
`;

export const Hero = styled.div`
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;
  padding: 32px 32px 0;

  h1 {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 28px;
    font-weight: 700;
  }

  p {
    margin-top: 6px;
    font-size: 15px;
    color: ${color.textSub};
  }
`;

export const Badge = styled.span`
  padding: 2px 8px;
  background-color: ${color.primaryLight};
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  color: ${color.primary};
`;

export const Main = styled.main`
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 6fr);
  gap: 24px;
  align-items: start;
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;
  padding: 24px 32px 48px;

  @media (max-width: 960px) {
    grid-template-columns: minmax(0, 1fr);
    padding: 16px 16px 40px;
  }
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const Footer = styled.footer`
  margin-top: auto;
  padding: 24px;
  font-size: 12px;
  text-align: center;
  color: ${color.textMuted};
`;

// Card
export const Card = styled.section`
  padding: 24px;
  background-color: #fff;
  border: 1px solid ${color.borderLight};
  border-radius: 12px;
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;

  h2 {
    font-size: 17px;
    font-weight: 700;
  }
`;

export const StepNumber = styled.span`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  background-color: ${color.primary};
  border-radius: 50%;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
`;

export const Optional = styled.span`
  font-size: 13px;
  color: ${color.textMuted};
`;

export const Hint = styled.p`
  margin-top: 12px;
  font-size: 13px;
  line-height: 1.6;
  color: ${color.textMuted};
`;

export const Notice = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  background-color: ${color.warnBg};
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.6;
  color: ${color.warnText};
`;

// Buttons
export const PrimaryButton = styled.button`
  width: 100%;
  height: 52px;
  background-color: ${color.primary};
  border-radius: 10px;
  font-size: 16px;
  font-weight: 700;
  color: #fff;

  &:hover:not(:disabled) {
    background-color: ${color.primaryHover};
  }

  &:disabled {
    background-color: ${color.border};
    color: ${color.textMuted};
    cursor: not-allowed;
  }
`;

export const SecondaryButton = styled.button`
  height: 36px;
  padding: 0 14px;
  background-color: #fff;
  border: 1px solid ${color.border};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${color.text};

  &:hover:not(:disabled) {
    border-color: ${color.primary};
    color: ${color.primary};
  }

  &:disabled {
    color: ${color.textMuted};
    cursor: not-allowed;
  }
`;

export const TextButton = styled.button`
  font-size: 14px;
  color: ${color.primary};
  text-decoration: underline;

  &:hover {
    color: ${color.primaryHover};
  }
`;

export const SubmitArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: center;

  small {
    font-size: 13px;
    color: ${color.textMuted};
  }
`;

// Form
export const Input = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid ${color.border};
  border-radius: 8px;
  font-size: 14px;
  color: ${color.text};

  &:focus {
    outline: none;
    border-color: ${color.primary};
  }

  &::placeholder {
    color: #9aa5ad;
  }
`;

export const Textarea = styled.textarea`
  width: 100%;
  min-height: 260px;
  padding: 12px;
  border: 1px solid ${color.border};
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.6;
  color: ${color.text};
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${color.primary};
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const Spinner = styled.span`
  display: inline-block;
  width: 32px;
  height: 32px;
  border: 3px solid ${color.primaryLight};
  border-top-color: ${color.primary};
  border-radius: 50%;
  animation: ${spin} 0.9s linear infinite;
`;
